/// <amd-module name="Repos/Editor/UserPreferencesService" />
import * as React from "react";
import { IEditorUserPreferencesService } from "Repos/Common/Contracts/Editor";
import { IVersionControlUserPreferences } from "Repos/Common/Contracts/VersionControlViewModel";
import { IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";
import * as Resources from "Repos/Editor/Resources";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext, Services, VssService } from "VSS/Platform/Context";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { EditorPreferencesPanel } from "./Components/EditorPreferencesPanel";

export interface IMonacoEditorUserPreferencesService extends IEditorUserPreferencesService {
    /**
     * Gets the action to be included in Monaco context menu.
     */
    getAction(isDiffEditor: boolean): monaco.editor.IActionDescriptor;

    /**
     * Gets the options for the editor from the current preferences.
     * @param editable
     * @param forceSingleLineMode
     * @param showMarginForIcons
     * @param hideLineNumbers
     */
    getEditorOptions(
        editable: boolean,
        forceSingleLineMode?: boolean,
        showMarginForIcons?: boolean,
        hideLineNumbers?: boolean
    ): monaco.editor.IEditorOptions;
}

class EditorUserPreferencesService extends VssService implements IMonacoEditorUserPreferencesService {
    private preferences: ObservableValue<IVersionControlUserPreferences>;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const vcViewModelService = pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        this.preferences = new ObservableValue(vcViewModelService.getUserPreferences());
    }

    public getEditorOptions(
        editable: boolean,
        forceSingleLineMode?: boolean,
        showMarginForIcons?: boolean,
        hideLineNumbers?: boolean
    ): monaco.editor.IDiffEditorOptions {
        const editorPreferences = this.preferences.value.editorPreferences;

        const options: monaco.editor.IDiffEditorOptions = {
            folding: editorPreferences.foldingEnabled && !forceSingleLineMode && !showMarginForIcons,
            glyphMargin: showMarginForIcons,
            lineDecorationsWidth: showMarginForIcons ? 20 : undefined,
            lineNumbers: hideLineNumbers ? "off" : "on",
            minimap: { enabled: editorPreferences.minimapEnabled },
            renderWhitespace: editorPreferences.whiteSpaceEnabled ? "all" : "none",
            ignoreTrimWhitespace: !editorPreferences.whiteSpaceEnabled,
            readOnly: !editable,
            wordWrap: editorPreferences.wordWrapEnabled && !forceSingleLineMode ? "on" : "off"
        };

        if (forceSingleLineMode) {
            options.find = {
                addExtraSpaceOnTop: false
            };
        }

        return options;
    }

    public getPreferences(): IReadonlyObservableValue<IVersionControlUserPreferences> {
        return this.preferences;
    }

    public getAction(isDiffEditor: boolean): monaco.editor.IActionDescriptor {
        return {
            id: "tfs-user-preferences",
            label: Resources.UserPreferences,
            contextMenuGroupId: "99_preferences",
            run: () => this.showPreferences(isDiffEditor)
        };
    }

    public showPreferences(isDiffEditor: boolean): void {
        this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss: () => void) => {
            const vcViewModelService = this.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
            const editorPreferences = vcViewModelService.getUserPreferences().editorPreferences;
            return (
                <EditorPreferencesPanel
                    initialPreferences={editorPreferences}
                    isDiffEditor={isDiffEditor}
                    onDismiss={newEditorPreferences => {
                        onDismiss();
                        if (newEditorPreferences) {
                            const newPreferences = { ...vcViewModelService.getUserPreferences(), editorPreferences: newEditorPreferences };
                            vcViewModelService.setUserPreferences(newPreferences);

                            this.preferences.value = newPreferences;
                        }
                    }}
                />
            );
        });
    }
}

export function getEditorUserPreferencesService(pageContext: IVssPageContext): IMonacoEditorUserPreferencesService {
    return pageContext.getService<IMonacoEditorUserPreferencesService>("IEditorUserPreferencesService");
}

Services.add("IEditorUserPreferencesService", { serviceFactory: EditorUserPreferencesService });
